// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ShareOptions } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import WishListSharingStatus, {
    WishListSharingStatusStyles,
} from "@insite/content-library/Components/WishListSharingStatus";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Star from "@insite/mobius/Icons/Star";
// import StarX from "@insite/mobius/Icons/StarX";
import Tag from "@insite/mobius/Icons/Tag";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import NonTranslationTypography from "@sc/App/Components/NonTranslationTypography/NonTranslationTypography";
import MyListsDetailsPageTypeLink from "@sc/Overrides/Components/MyListsDetailsPageTypeLink";
import StarX from "@sc/XDL/Icons/StarX";
import React, { useContext } from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    isUpdatingCart: state.context.isUpdatingCart,
    xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
});

interface OwnProps {
    wishList: WishListModel;
    extendedStyles?: WishListCardStyles;
    addWishListToCart: HandleThunkActionCreator<typeof addWishListToCart>;
    deleteWishList?: () => void;
    leaveWishList?: () => void;
    toggleFavorite?: () => void;
    toggleHide?: () => void;
    // XNG-Change
    displayProductsImages?: boolean;
    displayNumberOfItems?: boolean;
    showTagsAndFavoriteIcon?: boolean;
    hideDescription?: boolean;
    isMyListPage?: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface WishListCardStyles {
    gridContainer?: GridContainerProps;
    wishListInfoGridItem?: GridItemProps;
    wishListInfoGridContainer?: GridContainerProps;
    basicInfoGridItem?: GridItemProps;
    basicInfoGridContainer?: GridContainerProps;
    detailLinkGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyProps;
    lastUpdatedGridItem?: GridItemProps;
    lastUpdatedText?: TypographyProps;
    extendedInfoGridItem?: GridItemProps;
    extendedInfoGridContainer?: GridContainerProps;
    productImagesGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    productImage?: LazyImageProps;
    sharingStatusGridItem?: GridItemProps;
    sharingStatus?: WishListSharingStatusStyles;
    actionGridItem?: GridItemProps;
    actionGridMediumHidden?: HiddenProps;
    actionOverflowIcon?: IconPresentationProps;
    actionOverflowMenu?: OverflowMenuPresentationProps;
    actionAddToCartClickable?: ClickablePresentationProps;
    actionHideClickable?: ClickablePresentationProps;
    actionDeleteClickable?: ClickablePresentationProps;
    actionLeaveClickable?: ClickablePresentationProps;
    actionAddToCartButton?: ButtonPresentationProps;
    actionHideLink?: LinkPresentationProps;
    actionDeleteButton?: ButtonPresentationProps;
    actionLeaveButton?: ButtonPresentationProps;
    actionFavoriteClickable?: ClickablePresentationProps;
    basicIconGridContainer?: GridContainerProps;
    detailIconGridItem?: GridItemProps;
    starIcon?: IconPresentationProps;
    starXIcon?: IconPresentationProps;
    tagTooltip?: TooltipPresentationProps;
    // XNG-Change
    detailLink?: LinkPresentationProps;
    itemsSmallHeading?: SmallHeadingAndTextStyles;
    actionAddToCartLink?: LinkPresentationProps;
    actionDeleteLink?: LinkPresentationProps;
    actionLeaveLink?: LinkPresentationProps;
    viwListLink?: LinkPresentationProps;
}

export const wishListCardStyles: WishListCardStyles = {
    gridContainer: {
        gap: 5,
    },
    wishListInfoGridItem: {
        width: [10, 10, 8, 10, 10],
        align: "middle",
    },
    basicInfoGridItem: { width: [12, 12, 5, 4, 4] },
    basicInfoGridContainer: {
        gap: 10,
        css: css`
            margin-left: 10px;
        `,
    },
    detailLinkGridItem: { width: 12 },
    descriptionGridItem: { width: 12 },
    descriptionText: { ellipsis: true },
    lastUpdatedGridItem: { width: 12 },
    extendedInfoGridItem: {
        width: [12, 12, 7, 8, 8],
        align: "middle",
    },
    productImageLink: {
        css: css`
            margin-right: 5px;
        `,
    },
    productImagesGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            flex-wrap: wrap;
        `,
    },
    productImage: {
        css: css`
            max-width: 75px;
            img {
                height: 100%;
            }
        `,
        errorTypographyProps: {
            size: 11,
            css: css`
                max-height: 65px;
            `,
        },
    },
    sharingStatusGridItem: { width: [12, 12, 12, 4, 4] },
    actionGridItem: {
        width: [2, 2, 4, 2, 2],
        align: "top",
        css: css`
            justify-content: flex-end;
        `,
    },
    actionGridMediumHidden: {
        css: css`
            width: 100%;
        `,
    },
    actionAddToCartButton: {
        color: "secondary",
        css: css`
            white-space: nowrap;
            width: 100%;
            padding: 0 10px;
        `, // keep text from wrapping
    },
    actionHideLink: {
        css: css`
            display: block;
            margin: 10px auto 0 auto;
        `,
    },
    actionDeleteButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    actionLeaveButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    basicIconGridContainer: {
        gap: 10,
        css: css`
            width: 15px;
            flex-shrink: 0;
        `,
        offsetCss: css`
            flex-direction: column;
        `,
    },
    actionFavoriteClickable: {
        css: css`
            width: 15px;
        `,
    },
    starIcon: {
        src: Star,
        size: 15,
    },
    starXIcon: {
        src: StarX,
        size: 15,
    },
    tagTooltip: {
        iconProps: {
            src: Tag,
            size: 15,
            css: css`
                margin: 4px 0;
            `,
        },
    },
};

const WishListCard = ({
    language,
    allowEditingOfWishLists,
    isUpdatingCart,
    extendedStyles,
    wishList,
    addWishListToCart,
    deleteWishList,
    leaveWishList,
    toggleFavorite,
    toggleHide,
    // XNG-Change
    displayProductsImages = true,
    displayNumberOfItems = false,
    showTagsAndFavoriteIcon = true,
    hideDescription,
    isMyListPage,
    xGoogleAnalytics,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const isLoadingAnyList = useAppSelector(state => Object.keys(state.data.wishLists.isLoading).length > 0);

    const clickAddToCartHandler = (e: any) => {
        e.preventDefault();
        addWishListToCart({
            apiParameter: { wishListId: wishList.id },
            onSuccess: onAddAllToCartSuccess,
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.(resultProps.apiResult);
                } else if (resultProps.errorMessage) {
                    toasterContext.addToast({
                        body: resultProps.errorMessage,
                        messageType: "danger",
                    });
                }
            },
        });
    };

    const onAddAllToCartSuccess = (result: CartLineCollectionModel) => {
        toasterContext.addToast({
            body: result.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : translate("List Added to Cart"),
            messageType: "success",
        });
    };

    const updatedOnDisplay = getLocalizedDateTime({
        dateTime: new Date(wishList.updatedOn),
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    // let lastUpdatedDisplay = `${translate("Updated")} ${updatedOnDisplay}`;
    // if (wishList.updatedByDisplayName && !wishList.isAutogenerated) {
    //     lastUpdatedDisplay += ` ${translate("by")} ${wishList.updatedByDisplayName}`;
    // }
    const [styles] = React.useState(() => mergeToNew(wishListCardStyles, extendedStyles));

    // XNG-Change
    let lastUpdatedDisplay = (
        <StyledWrapper>
            <Typography>{translate("Updated")}</Typography>&nbsp;
            <NonTranslationTypography>{updatedOnDisplay}</NonTranslationTypography>
        </StyledWrapper>
    );
    if (wishList.updatedByDisplayName && !wishList.isAutogenerated) {
        lastUpdatedDisplay = (
            <StyledWrapper>
                <Typography>{translate("Updated")}</Typography> &nbsp;
                <NonTranslationTypography>{updatedOnDisplay}</NonTranslationTypography> &nbsp;
                <Typography>{translate("by")}</Typography>&nbsp;
                <NonTranslationTypography>{wishList.updatedByDisplayName}</NonTranslationTypography>
            </StyledWrapper>
        );
    }
    // Xng-Changes: remove "list" from translates
    const canAddToCart = wishList.canAddToCart && !!wishList.wishListLinesCount && wishList.wishListLinesCount > 0;
    const addToCartLabel = translate("Add to Cart");
    // Xng-Change
    const viewListLabel = translate("View List");

    const toggleHideButtonText = wishList.isHidden ? translate("Unhide") : translate("Hide");
    const toggleFavoriteLabel = wishList.isFavorite
        ? translate("Remove from favorites")
        : translate("Add to favorites");
    const deleteLabel = translate("Delete");
    const leaveLabel = translate("Leave");

    // XNG-Changes: Google Analytics - Track wishlist link clicks
    const trackWishListClick = (clickText: string) => {
        if (xGoogleAnalytics?.myacctMylistsEvent && xGoogleAnalytics?.myacctMylistsEventName) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: xGoogleAnalytics.myacctMylistsEventName,
                list_name: wishList.name || "",
                click_text: clickText,
                affiliation: xGoogleAnalytics.affiliation || "",
                sales_rep: xGoogleAnalytics.salesRep || "",
                is_rep: xGoogleAnalytics.isRep || "",
                bill_to_num: xGoogleAnalytics.billToNum || "",
                ship_to_num: xGoogleAnalytics.shipToNum || "",
                cust_type: xGoogleAnalytics.customerType || "",
                login_type: xGoogleAnalytics.loginType || "",
                warehouse: xGoogleAnalytics.warehouse || "",
            };

            dataLayer.push({ ecommerce: null });
            dataLayer.push(googleAnalyticsData);
        }
    };

    return (
        <GridContainer {...styles.gridContainer} data-test-selector="wishListCard">
            <GridItem {...styles.wishListInfoGridItem} data-test-selector={`${wishList.name}`}>
                <GridContainer {...styles.wishListInfoGridContainer}>
                    <GridItem {...styles.basicInfoGridItem}>
                        {/* {showTagsAndFavouriteIcon && (
                            <>
                                <GridContainer {...styles.basicIconGridContainer}>
                                    {!wishList.isAutogenerated && (
                                        <GridItem {...styles.detailIconGridItem}>
                                            <Clickable
                                                {...styles.actionFavoriteClickable}
                                                onClick={toggleFavorite}
                                                aria-label={toggleFavoriteLabel}
                                                data-test-selector={`myListsFavorite_${wishList.id}`}
                                                disabled={isLoadingAnyList}
                                            >
                                                {wishList.isFavorite && (
                                                    <Icon
                                                        {...styles.starXIcon}
                                                        data-test-selector={`myListsFavoriteStarXIcon_${wishList.id}`}
                                                    />
                                                )}
                                                {!wishList.isFavorite && (
                                                    <Icon
                                                        {...styles.starIcon}
                                                        data-test-selector={`myListsFavoriteStarIcon_${wishList.id}`}
                                                    />
                                                )}
                                            </Clickable>
                                        </GridItem>
                                    )}
                                    {wishList.wishListTags && wishList.wishListTags.length > 0 && (
                                        <GridItem
                                            {...styles.detailIconGridItem}
                                            data-test-selector={`myListsTags_${wishList.id}`}
                                        >
                                            <Tooltip
                                                {...styles.tagTooltip}
                                                text={`${translate("Tags Assigned")}: ${wishList.wishListTags
                                                    .map(o => o.tag)
                                                    .join(", ")}`}
                                            />
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </>
                        )} */}

                        <GridContainer {...styles.basicInfoGridContainer}>
                            <GridItem {...styles.detailLinkGridItem}>
                                <MyListsDetailsPageTypeLink
                                    title={wishList.name}
                                    wishListId={wishList.id}
                                    testSelector="wishListCardName"
                                    // XNG-Change
                                    extendedStyles={styles.detailLink}
                                    onClick={() => trackWishListClick(wishList.name || "")}
                                />
                            </GridItem>
                            {!hideDescription && wishList.description && (
                                <GridItem {...styles.descriptionGridItem}>
                                    <Typography
                                        {...styles.descriptionText}
                                        data-test-selector="wishListCardDescription"
                                    >
                                        {wishList.description}
                                    </Typography>
                                </GridItem>
                            )}
                            <GridItem {...styles.lastUpdatedGridItem}>
                                <Typography {...styles.lastUpdatedText} data-test-selector="wishListCardLastUpdated">
                                    {lastUpdatedDisplay}
                                </Typography>
                            </GridItem>
                            {showTagsAndFavoriteIcon && (
                                <GridItem {...styles.sharingStatusGridItem}>
                                    {isMyListPage && (
                                        <WishListSharingStatus
                                            extendedStyles={styles.sharingStatus}
                                            wishList={wishList}
                                        />
                                    )}
                                    <GridContainer {...styles.basicIconGridContainer}>
                                        {wishList.wishListTags && wishList.wishListTags.length > 0 && (
                                            <GridItem
                                                {...styles.detailIconGridItem}
                                                data-test-selector={`myListsTags_${wishList.id}`}
                                            >
                                                <Tooltip
                                                    {...styles.tagTooltip}
                                                    text={`${translate("Tags Assigned")}: ${wishList.wishListTags
                                                        .map(o => o.tag)
                                                        .join(", ")}`}
                                                />
                                            </GridItem>
                                        )}
                                        {!wishList.isAutogenerated && (
                                            <GridItem {...styles.detailIconGridItem}>
                                                {/* xng-change */}
                                                <Link
                                                    {...styles.actionFavoriteClickable}
                                                    onClick={toggleFavorite}
                                                    data-test-selector={`myListsFavorite_${wishList.id}`}
                                                    disabled={isLoadingAnyList}
                                                    aria-label={toggleFavoriteLabel}
                                                    icon={{
                                                        iconProps: {
                                                            src: wishList.isFavorite ? StarX : Star,
                                                        },
                                                    }}
                                                >
                                                    {/* {wishList.isFavorite && (
                                                        <Icon
                                                            {...styles.starXIcon}
                                                            data-test-selector={`myListsFavoriteStarXIcon_${wishList.id}`}
                                                        />
                                                    )}
                                                    {!wishList.isFavorite && (
                                                        <Icon
                                                            {...styles.starIcon}
                                                            data-test-selector={`myListsFavoriteStarIcon_${wishList.id}`}
                                                        />
                                                    )} */}
                                                    {translate("Favorite")}
                                                </Link>
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.extendedInfoGridItem}>
                        <GridContainer {...styles.extendedInfoGridContainer}>
                            {/* XNG-Change */}
                            {displayProductsImages && (
                                <GridItem {...styles.productImagesGridItem} data-test-selector="productImagesGridItem">
                                    {wishList.wishListLineCollection &&
                                        wishList.wishListLineCollection.length > 0 &&
                                        wishList.wishListLineCollection.slice(0, 3).map(line => (
                                            <Link
                                                key={line.id.toString()}
                                                {...styles.productImageLink}
                                                href={line.productUri}
                                            >
                                                <LazyImage
                                                    {...styles.productImage}
                                                    src={line.smallImagePath}
                                                    altText={line.altText}
                                                    title={line.altText}
                                                />
                                            </Link>
                                        ))}
                                </GridItem>
                            )}

                            {/* XNG-Change: display wishlistLinesCount */}
                            <GridItem {...styles.sharingStatusGridItem}>
                                {displayNumberOfItems &&
                                    wishList.properties.wishlistLinesCount &&
                                    wishList.properties.wishlistLinesCount !== "0" && (
                                        <SmallHeadingAndText
                                            heading={translate("Items")}
                                            text={wishList.properties.wishlistLinesCount}
                                            extendedStyles={styles.itemsSmallHeading}
                                        />
                                    )}
                                {!isMyListPage && (
                                    <WishListSharingStatus extendedStyles={styles.sharingStatus} wishList={wishList} />
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.actionGridItem}>
                {/* <Hidden above="sm">
                    <OverflowMenu position="end" {...styles.actionOverflowMenu}>
                        <Clickable
                            {...styles.actionAddToCartClickable}
                            disabled={!canAddToCart || isUpdatingCart}
                            onClick={clickAddToCartHandler}
                        >
                            {addToCartLabel}
                        </Clickable>
                        {!wishList.isAutogenerated && (
                            <Clickable {...styles.actionHideClickable} disabled={isLoadingAnyList} onClick={toggleHide}>
                                {toggleHideButtonText}
                            </Clickable>
                        )}
                        {allowEditingOfWishLists &&
                            !!deleteWishList &&
                            !wishList.isSharedList &&
                            !wishList.isAutogenerated && (
                                <Clickable {...styles.actionDeleteClickable} onClick={deleteWishList}>
                                    {deleteLabel}
                                </Clickable>
                            )}
                        {allowEditingOfWishLists &&
                            !!leaveWishList &&
                            wishList.isSharedList &&
                            wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                                <Clickable {...styles.actionLeaveClickable} onClick={leaveWishList}>
                                    {leaveLabel}
                                </Clickable>
                            )}
                    </OverflowMenu>
                </Hidden> */}
                {/* <Hidden {...styles.actionGridMediumHidden} below="md"> */}
                {/* XNG-Change: use MyListsDetailsPageTypeLink */}
                <MyListsDetailsPageTypeLink
                    title={viewListLabel}
                    wishListId={wishList.id}
                    testSelector="wishListCardName"
                    extendedStyles={styles.viwListLink}
                    onClick={() => trackWishListClick(viewListLabel)}
                />
                {!wishList.isAutogenerated && (
                    <Link
                        {...styles.actionHideLink}
                        onClick={() => {
                            trackWishListClick(toggleHideButtonText);
                            toggleHide?.();
                        }}
                        data-test-selector={`myListsHide_${wishList.id}`}
                        disabled={isLoadingAnyList}
                        aria-label={toggleHideButtonText}
                    >
                        {toggleHideButtonText}
                    </Link>
                )}
                {allowEditingOfWishLists && !!deleteWishList && !wishList.isSharedList && !wishList.isAutogenerated && (
                    <Link
                        {...styles.actionDeleteLink}
                        onClick={deleteWishList}
                        data-test-selector={`wishListCardDeleteButton_${wishList.id}`}
                        aria-label={deleteLabel}
                    >
                        {deleteLabel}
                    </Link>
                )}
                {allowEditingOfWishLists &&
                    !!leaveWishList &&
                    wishList.isSharedList &&
                    wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                        <Link
                            {...styles.actionLeaveLink}
                            onClick={leaveWishList}
                            data-test-selector={`wishListCardLeaveButton_${wishList.id}`}
                            aria-label={leaveLabel}
                        >
                            {leaveLabel}
                        </Link>
                    )}
                {/* </Hidden> */}
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(WishListCard);
